import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type { RootState } from "../app/store";
import { logout, tokenRefreshed } from "../app/slices/authSlice";
import { logger } from "../lib/logger";

// Custom base query with auth and error handling
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || "/api",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    headers.set("X-Request-ID", crypto.randomUUID());
    return headers;
  },
});

// Base query with re-auth logic
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Try to refresh the token
    const refreshToken = (api.getState() as RootState).auth.refreshToken;

    if (refreshToken) {
      const refreshResult = await baseQuery(
        {
          url: "/auth/refresh",
          method: "POST",
          body: { refreshToken },
        },
        api,
        extraOptions,
      );

      if (refreshResult.data) {
        // Type guard for refresh response
        const data = refreshResult.data as Record<string, unknown>;
        if (typeof data.token === "string") {
          api.dispatch(tokenRefreshed(data.token));
          // Retry the original request
          result = await baseQuery(args, api, extraOptions);
        }
      } else {
        api.dispatch(logout());
        logger.warn("Session expired, user logged out");
      }
    } else {
      api.dispatch(logout());
    }
  }

  if (result.error) {
    const url = typeof args === "string" ? args : args.url;
    logger.error("API request failed", {
      url,
      status: result.error.status,
      data: result.error.data,
    });
  }

  return result;
};

// Create the API slice
export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Borefield",
    "Bore",
    "Job",
    "Report",
    "Rig",
    "Survey",
    "ThermalTest",
    "Circuit",
    "User",
    "TestEquipment",
    "CalEvent",
    "CaloricTest",
    // Appended additively for the well-creation spine (V1-26) — wellApi cache tags.
    "Well",
    "Wellbore",
    // Appended additively for the Pad layer (V1-26 pad-first onboarding) — padApi cache tags.
    "Pad",
    // Appended additively for GeoNames geographic picklists (geonamesApi) — country/admin children.
    "Geo",
    // Appended additively for the downhole completion model (completionsApi) — Completion→String→
    // Component cache tags. See features/completions (DESIGN_downhole_completion_model.md).
    "Completion",
    "CompletionString",
    "CompletionComponent",
    // Appended additively for the cellars feature (below-grade wellhead grouping) — cellarApi cache
    // tags. See features/cellars (docs/DESIGN_cellars.md).
    "Cellar",
    // Appended additively for the AFE register (RM-14) — afeApi cache tags. See features/jobs/store/
    // afeApi.ts + docs/DESIGN_afe.md.
    "Afe",
    // Appended additively for Work Plans (WoP) — wopApi cache tags. `WopPlan` covers every
    // revision in a lineage; `WopCatalog` is the org's reference-catalog additions, which change on
    // a different cadence from the plans that cite them and must not invalidate them.
    // See features/wop/store/wopApi.ts + docs/DESIGN_wop.md.
    "WopPlan",
    "WopCatalog",
    // Appended additively for pad campaigns (PJ-01) — padJobApi cache tags. `PadJob` covers the
    // campaign record; `PadJobMember` is the SERVER-COMPUTED membership projection over
    // Job.padJobId, which changes when a member job is written rather than when the campaign is,
    // so it must invalidate on a different trigger from the campaign itself.
    // See features/jobs/store/padJobApi.ts + docs/DESIGN_pad_campaigns_corpus.md.
    "PadJob",
    "PadJobMember",
  ],
  endpoints: () => ({}),
});

// Export hooks for usage in components
export const {
  middleware: apiMiddleware,
  reducer: apiReducer,
  reducerPath,
} = apiSlice;
